import type { ReactNode } from 'react';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~/components/ui/sheet';
import { cn } from '~/utils/cn';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** `wide` for Compare; everything else is the standard detail width. */
  width?: 'default' | 'wide';
  children: ReactNode;
  footer?: ReactNode;
  /** Content that brings its own padding (the batch detail) — the body adds none. */
  flush?: boolean;
  /**
   * `panel` focuses the sheet itself instead of its first control — for content whose primary
   * action renders after a read, so the ring never lands on a secondary button meanwhile.
   */
  initialFocus?: 'first' | 'panel';
}

/**
 * The Batches page's one side-panel frame (detail, filters, fix & retry, compare): a right sheet
 * — full width on mobile — with a fixed header, a scrolling body and a pinned footer. Radix traps
 * focus while open, closes on Esc and hands focus back to the trigger.
 */
export function BatchSidePanel({
  open,
  onOpenChange,
  title,
  description,
  width = 'default',
  children,
  footer,
  flush = false,
  initialFocus = 'first',
}: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'flex w-full flex-col gap-0 p-0 outline-none',
          width === 'wide' ? 'sm:max-w-4xl' : 'sm:max-w-lg',
        )}
        onOpenAutoFocus={(event) => {
          if (initialFocus !== 'panel') return;
          event.preventDefault();
          // Radix's focus scope makes the content itself focusable (tabIndex -1); Tab then
          // starts at the first control, Esc still closes.
          if (event.currentTarget instanceof HTMLElement) event.currentTarget.focus();
        }}
      >
        <SheetHeader className="border-b px-6 py-4 pr-12">
          <SheetTitle className="text-base">{title}</SheetTitle>
          {description ? (
            <SheetDescription asChild>
              <div>{description}</div>
            </SheetDescription>
          ) : null}
        </SheetHeader>
        <div className={cn('min-h-0 flex-1 overflow-y-auto', !flush && 'px-6 py-5')}>
          {children}
        </div>
        {footer ? (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t px-6 py-3">
            {footer}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
